/**
 * Opt-in editor features, shared by every surface that mounts RichTextEditor.
 *
 *   pageBreakButtons  Page break / New page buttons (and Ctrl+Enter)
 *   typingShortcuts   abbreviation expansion, plus the Shortcuts button
 *   dictation         the record-audio button
 *   saveShortcut      Ctrl+S runs the surface's own save
 *   printShortcut     Ctrl+P runs the surface's own print, not the browser's
 *   wordCount         the "N words" footer under the editor
 *   justifyIndent     justify, outdent and indent buttons
 *
 * A surface lists what it enables; everything defaults to off, and a feature
 * that is off changes nothing - not the toolbar, not the saved HTML. Features
 * never touch the presets in presets.js: those define the schema, and with it
 * what a document round-trips as. Anything a feature needs (an input rule, a
 * command) is added on top of the preset.
 *
 * Features are resolved once per mount, like the extensions they bring: the
 * schema cannot change after mount. Remount with a different `key` to change
 * them.
 */
import parseToolbar from "./parseToolbar";

export const FEATURE_NAMES = [
  "pageBreakButtons",
  "typingShortcuts",
  "dictation",
  "saveShortcut",
  "printShortcut",
  "wordCount",
  "justifyIndent",
];

/** Every feature, true or false. Unknown keys are ignored. */
export function resolveFeatures(features = {}) {
  const resolved = {};
  FEATURE_NAMES.forEach((name) => {
    resolved[name] = Boolean(features[name]);
  });
  return resolved;
}

const stringify = (groups) => groups.map((group) => group.join(" ")).join(" | ");

/**
 * The toolbar string with the buttons the enabled features bring, each put
 * where the order sheet has it. Names already in the toolbar are left alone,
 * so a surface that already offers justify or indent is unchanged.
 */
export function withFeatureItems(toolbar, features = {}) {
  const enabled = resolveFeatures(features);
  const groups = parseToolbar(toolbar).map((group) => group.slice());
  const has = (name) => groups.some((group) => group.indexOf(name) !== -1);
  const groupOf = (name) => groups.find((group) => group.indexOf(name) !== -1);
  const insertGroupAfter = (anchor, group) => {
    const at = groups.findIndex((g) => g.indexOf(anchor) !== -1);
    groups.splice(at === -1 ? groups.length : at + 1, 0, group);
  };

  if (enabled.justifyIndent) {
    if (!has("alignjustify")) {
      const align = groupOf("alignright") || groupOf("alignleft");
      if (align) align.splice(align.indexOf("alignright") + 1 || align.length, 0, "alignjustify");
      else groups.push(["alignjustify"]);
    }
    const missing = ["outdent", "indent"].filter((name) => !has(name));
    if (missing.length) insertGroupAfter("numlist", missing);
  }
  if (enabled.pageBreakButtons) {
    const missing = ["pagebreak", "newpage"].filter((name) => !has(name));
    const tables = groupOf("table");
    if (missing.length && tables) tables.push(...missing);
    else if (missing.length) groups.push(missing);
  }
  if (enabled.typingShortcuts && !has("ordershortcuts")) groups.push(["ordershortcuts"]);
  if (enabled.dictation && !has("recordaudio")) groups.push(["recordaudio"]);

  return stringify(groups);
}

/**
 * Words in an HTML string, the way the order sheet's footer has always
 * counted them: tags and &nbsp; become spaces, then whitespace splits.
 */
export function countWords(html) {
  const text = String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim();
  return text ? text.split(/\s+/).length : 0;
}
